import { FC, useState, useEffect } from 'react';
import { Box, IconButton, MenuItem, Typography } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import BaseDialog from '@/components/ui/BaseDialog';
import BaseTextField from '@/components/form/BaseTextField';
import { useTranslation } from 'react-i18next';

const layoutPurposeOptions = [
    { value: 1, label: 'Normal Operation' },
    { value: 2, label: 'No Product Layout' },
    { value: 3, label: 'Low Battery Layout' },
    // { value: 4, label: 'No Index Layout' },
    // { value: 5, label: 'Deassociated Layout' },
    { value: 6, label: 'Out of Stock Layout' },
    { value: 7, label: 'Campaign Layout' },
];

interface LayoutDialogProps {
    open: boolean;
    layoutNames: string[];
    initialData?: {
        name: string;
        purpose: number;
        content: string;
    };
    dialogTitle: string;
    onSave: (data: { name: string; purpose: number; content: string; capacity: number }) => void;
    onClose: () => void;
    showTopRightClose?: boolean;
}

const LayoutDialog: FC<LayoutDialogProps> = ({
    open,
    layoutNames = [],
    initialData,
    dialogTitle,
    onSave,
    onClose,
    showTopRightClose = false,
}) => {
    const { t } = useTranslation('common');
    const [name, setName] = useState('');
    const [purpose, setPurpose] = useState<number>(1);
    const [content, setContent] = useState('');
    const [helpOpen, setHelpOpen] = useState(false);
    const [nameError, setNameError] = useState('');
    const [contentError, setContentError] = useState('');

    useEffect(() => {
        const existNames = layoutNames.filter((o) => o != initialData?.name);
        if (!name) {
            setNameError('Layout Name is required');
            return;
        }

        if (existNames.includes(name)) {
            setNameError('Layout name already exists');
            return;
        }
        setNameError('');
    }, [initialData, layoutNames, name]);

    useEffect(() => {
        if (!content) {
            setContentError('Layout Contents are required');
            return;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'application/xml');
        if (doc.getElementsByTagName('parsererror').length > 0) {
            setContentError('Invalid XML');
            return;
        }

        setContentError('');
    }, [content, t]);

    const handleConfirm = () => {
        if (nameError || contentError) return;
        onSave({ name, purpose, content, capacity: 1 });
    };

    const clearState = () => {
        setName('');
        setPurpose(1);
        setContent('');
        setHelpOpen(false);
        setNameError('Layout Name is required');
        setContentError('Layout Contents are required');
    };

    return (
        <>
            <BaseDialog
                open={open}
                maxWidth="md"
                dialogTitle={dialogTitle}
                confirmButtonText={t('Save')}
                cancelButtonText={t('Cancel')}
                showTopRightClose={showTopRightClose}
                onConfirm={handleConfirm}
                onClose={onClose}
                disableConfirm={nameError !== '' || contentError !== ''}
                showSpinnerOnDisable={false}
                TransitionProps={{
                    onEnter: () => {
                        if (initialData) {
                            setName(initialData.name);
                            setPurpose(initialData.purpose);
                            setContent(initialData.content);
                        }
                    },
                    onExited: clearState,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        gap: 2,
                        pt: 1,
                    }}
                >
                    <BaseTextField
                        label={t('Layout Name')}
                        value={name}
                        onChange={(e) => setName(e.target.value.trim())}
                        fullWidth
                        required
                        error={nameError !== ''}
                        helperText={t(nameError)}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            width: '100%',
                        }}
                    >
                        <BaseTextField
                            select
                            label={t('Layout Purpose')}
                            value={purpose}
                            onChange={(e) => setPurpose(Number(e.target.value))}
                            fullWidth
                        >
                            {layoutPurposeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {t(option.label)}
                                </MenuItem>
                            ))}
                        </BaseTextField>
                        <IconButton
                            size="small"
                            onClick={() => setHelpOpen(true)}
                            sx={{
                                backgroundColor: (theme) => theme.palette.warning.main,
                                color: 'common.white',
                                p: 0.5,
                                borderRadius: '50%',
                                '&:hover': {
                                    backgroundColor: (theme) => theme.palette.warning.dark,
                                },
                            }}
                            aria-label={t('Help')}
                        >
                            <HelpOutline fontSize="small" />
                        </IconButton>
                    </Box>

                    <BaseTextField
                        label={t('Layout Contents')}
                        multiline
                        rows={15}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        fullWidth
                        sx={{
                            '& .MuiInputBase-inputMultiline': {
                                overflowY: 'auto',
                            },
                        }}
                        required
                        error={contentError !== ''}
                        helperText={t(contentError)}
                    />
                </Box>
            </BaseDialog>

            <BaseDialog
                open={helpOpen}
                fullWidth
                maxWidth="sm"
                dialogTitle={t('Layout Purpose Guide')}
                onClose={() => setHelpOpen(false)}
                dialogContentProps={{
                    sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    },
                }}
            >
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {t('Normal Operation')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t(
                            'Choose “Normal Operation” for the standard layout used to display product information in everyday use.',
                        )}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {t('No Product Layout')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('Use this when a label is linked to a product that does not exist in the AURA system.')}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {t('Low Battery Layout')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('Use this for the screen shown when a label’s battery is low and needs replacement.')}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {t('Out of Stock Layout')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t(
                            'Use this to indicate that a product is out of stock on the shelf. This applies only when stock data is available in AURA.',
                        )}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                        {t('Note: “Out of Stock Layout” requires stock information to be available in AURA.')}
                    </Typography>
                </Box>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {t('Campaign Layout')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('Use this layout for time-bound or event-driven campaigns applied to labels.')}
                        <br />
                        {t(
                            'Campaign Layouts can be used to highlight promotions, special pricing, seasonal messages, brand initiatives, or other store-defined campaigns that are independent of normal product display.',
                        )}
                        <br />
                        {t(
                            'Campaigns may target specific products, shelves, or store areas and are typically activated for a defined period.',
                        )}
                    </Typography>
                </Box>
            </BaseDialog>
        </>
    );
};

export default LayoutDialog;
